import React, { useEffect, useMemo, useState } from "react";
import LabeledInput from "@/react/components/LabeledInput";
import { CountryFlags } from "@/react/features/support-tools/dialplans/CountryFlags";
import styled from "styled-components";
import { RuleSet } from "@/react/features/support-tools/dialplans/ui-components/RuleSet";
import Button from "@8x8/oxygen-button";
import { SpaceBetweenContainer } from "@/react/features/support-tools/dialplans/styled";
import { RULE_SET_TYPES } from "@/react/features/support-tools/dialplans/constants";
import Select from "@8x8/oxygen-select";
import { COLORS } from "@/react/theme/constants";
import Tooltip from "@8x8/oxygen-tooltip";
import { IconButton } from "@8x8/oxygen-icon-button";
import CloseIcon from "@8x8/oxygen-icon/icons/CloseIcon";
import { ErrorWithTooltipIcon } from "@/react/features/support-tools/dialplans/ui-components/ErrorWithTooltipIcon";
import FileUpload from "@/react/features/transcribe/FileUpload";
import { useConvertXmlFileToDialplanMutation } from "@/react/features/support-tools/dialplans/xmlBasedApi";
import { DownloadIcon } from "@8x8/oxygen-icon";
import { useDialPlanChangeControls } from "@/react/features/support-tools/dialplans/useDialPlanChangeControls";
import useDialplansApi from "@/react/features/support-tools/dialplans/useDialplansApi";

const NameContainer = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 64px;
`;

const CodesContainer = styled.div`
  margin-top: 16px;
  display: flex;
  gap: 8px;
`;

const RuleSetContainer = styled.div`
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const NewRuleSetSelectContainer = styled.div`
  background: ${COLORS.secondary};
  padding: 8px 16px;
  border-radius: 4px;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 16px;
  align-items: center;
`;

const NUMBER = "number";
const VALID_MIME_TYPES = Object.freeze(["application/xml", "text/xml"]);
const INFO_MESSAGES = Object.freeze([
  "Convert XML file to editable dialplan",
  "Drag and drop XML file here, or click to select a file",
  "File extensions accepted: .xml",
]);

const DialplanForm = ({
  initialDialplan,
  menuPortalTarget,
  isActionTriggered,
  action,
  dialplans,
}) => {
  const [uploadXmlFile] = useConvertXmlFileToDialplanMutation();
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [dialplan, setDialplan] = useState({});

  useEffect(() => {
    setDialplan(initialDialplan);
  }, [initialDialplan]);

  useEffect(() => {
    isActionTriggered && action(dialplan);
  }, [isActionTriggered]);

  const undefinedRuleSetTypes = useMemo(
    () =>
      RULE_SET_TYPES.filter(
        (ruleSetType) =>
          !dialplan?.ruleSet
            ?.map((ruleSet) => ruleSet.ruleSetType)
            ?.includes(ruleSetType)
      ),
    [dialplan]
  );

  const isNameUnique = useMemo(
    () =>
      !dialplans?.some(
        (_dialplan) =>
          _dialplan.name === dialplan.name && _dialplan.id !== dialplan.id
      ),
    [dialplan, dialplans]
  );

  const {
    handleAddRuleSet,
    onDeleteRuleSet,
    onChangeRegExpr,
    onChangeReplace,
    onChangeDescription,
    onAddNormalizationRule,
    onDeleteNormalizationRule,
    onRuleSetTypeChanged,
  } = useDialPlanChangeControls({ dialplan, setDialplan });

  const { handleImportXml } = useDialplansApi({
    uploadXmlFile,
    setDialplan,
    setIsUploadingFile,
  });

  return isUploadingFile ? (
    <FileUpload
      onFileUpload={handleImportXml}
      isUploaded={false}
      validMimeTypes={VALID_MIME_TYPES}
      infoMessages={INFO_MESSAGES}
    />
  ) : (
    <>
      <Button
        onClick={() => setIsUploadingFile(true)}
        variant={"tertiary"}
        iconLeft={<DownloadIcon />}
      >
        Import XML
      </Button>
      <NameContainer>
        <LabeledInput
          prompt={"Name"}
          placeholder={"Type name..."}
          value={dialplan.name}
          onChange={(e) => setDialplan({ ...dialplan, name: e.target.value })}
          hasError={!isNameUnique}
          iconRight={() => <ErrorWithTooltipIcon isNameUnique={isNameUnique} />}
        />
        <CountryFlags countryCode={dialplan.countryCode} />
      </NameContainer>
      <CodesContainer>
        <LabeledInput
          prompt={"Country Code"}
          type={NUMBER}
          value={dialplan.countryCode}
          onChange={(e) =>
            setDialplan({ ...dialplan, countryCode: e.target.value })
          }
        />
        <LabeledInput
          prompt={"Intl. Prefix"}
          type={NUMBER}
          value={dialplan.internationalPrefix}
          onChange={(e) =>
            setDialplan({ ...dialplan, internationalPrefix: e.target.value })
          }
        />
        <LabeledInput
          prompt={"National Prefix"}
          type={NUMBER}
          value={dialplan.nationalPrefix}
          onChange={(e) =>
            setDialplan({ ...dialplan, nationalPrefix: e.target.value })
          }
        />
      </CodesContainer>
      <hr />
      <SpaceBetweenContainer>
        <div>Rule Set</div>
        <Button variant={"tertiary"} onClick={handleAddRuleSet}>
          Add
        </Button>
      </SpaceBetweenContainer>
      <RuleSetContainer>
        {dialplan.ruleSet?.map((ruleSet, index) =>
          ruleSet.ruleSetType ? (
            <RuleSet
              {...ruleSet}
              isEditable={true}
              onChangeRegExpr={onChangeRegExpr(index)}
              onChangeReplace={onChangeReplace(index)}
              onChangeDescription={onChangeDescription(index)}
              onAddNormalizationRule={() => onAddNormalizationRule(index)}
              isDeletable={dialplan.ruleSet.length > 1}
              onDeleteRuleSet={(e) => onDeleteRuleSet(e, index)}
              onDeleteNormalizationRule={onDeleteNormalizationRule(index)}
            />
          ) : (
            <NewRuleSetSelectContainer>
              <Tooltip title={"Selecting a type is required!"}>
                <Select
                  onChange={(opt) => onRuleSetTypeChanged(opt, index)}
                  placeholder={"Select type..."}
                  options={undefinedRuleSetTypes.map((ruleSetType) => ({
                    label: ruleSetType,
                    value: ruleSetType,
                  }))}
                  hasError={true}
                  menuPortalTarget={menuPortalTarget}
                  menuPlacement={"top"}
                />
              </Tooltip>
              {dialplan?.ruleSet.length > 1 && (
                <IconButton onClick={(e) => onDeleteRuleSet(e, index)}>
                  <CloseIcon size={28} />
                </IconButton>
              )}
            </NewRuleSetSelectContainer>
          )
        )}
      </RuleSetContainer>
    </>
  );
};

export default DialplanForm;
